/**
 * Rows of the Unassigned panel under a pitch (SE-8391, PV23 · D4).
 *
 * A formation change never force-places anyone: members whose slot the new
 * formation lacks stay in the list with their rank and are listed here with
 * the slot they held. Place ▾ offers the current formation's compatible slots
 * nobody occupies; dragging onto any slot still works through the field.
 * @module features/scouting/utils/pitchUnassigned
 */
import type { TacticalSlot } from "@/generated/graphql";

import { isEligibleForSlot } from "./threeWeakest";

/** The member fields the projection reads — the Pitch member and scenario arrival both carry them. */
export interface PitchUnassignedMember {
  readonly playerId: string;
  readonly displayName: string;
  readonly rank: number;
  readonly gpr?: number | null;
  readonly effectiveSlot?: TacticalSlot | null;
  readonly naturalPositions: ReadonlyArray<{
    readonly abbreviation?: string | null;
  }>;
}

/** One row of the Unassigned panel. */
export interface PitchUnassignedEntry {
  readonly playerId: string;
  readonly displayName: string;
  /** The member's list rank — unchanged by the move (R04). */
  readonly rank: number;
  readonly gpr: number | null;
  /** The slot held before the formation change, when known. */
  readonly formerSlot: TacticalSlot | null;
  /**
   * Compatible slots of the current formation. SE-8626: a slot holds any
   * number of targets, so a held slot is offered too.
   */
  readonly placeSlots: readonly TacticalSlot[];
}

/** What the projection needs beside the members. */
export interface PitchUnassignedContext {
  /** Every slot of the current formation, in field order. */
  readonly assignmentSlots: readonly TacticalSlot[];
  /** Former slot per orphaned member (the controller's memory). */
  readonly formerSlotFor: (playerId: string) => TacticalSlot | undefined;
  /**
   * Offer every slot of the formation instead of the compatible ones
   * (SE-8494, the scenario pitch): there a slot is a ranked group that holds
   * many occupants, and an arrival is orphaned exactly when no compatible
   * slot exists — the compatible list would always be empty.
   */
  readonly offerEverySlot?: boolean;
}

/**
 * Projects the panel rows from the rendered members.
 * @param members - Members in rank order, with their effective slot
 * @param context - The formation's slots and the former-slot memory
 * @returns One row per member without a slot, in rank order
 */
export const projectPitchUnassigned = (
  members: readonly PitchUnassignedMember[],
  context: PitchUnassignedContext
): readonly PitchUnassignedEntry[] => {
  return members
    .filter(member => !member.effectiveSlot)
    .sort((left, right) => left.rank - right.rank)
    .map(member => {
      const abbreviations = member.naturalPositions.flatMap(position =>
        position.abbreviation ? [position.abbreviation] : []
      );
      return {
        playerId: member.playerId,
        displayName: member.displayName,
        rank: member.rank,
        gpr: typeof member.gpr === "number" ? member.gpr : null,
        formerSlot: context.formerSlotFor(member.playerId) ?? null,
        placeSlots: context.offerEverySlot
          ? context.assignmentSlots
          : context.assignmentSlots.filter(slot =>
              isEligibleForSlot(abbreviations, slot)
            ),
      };
    });
};
